import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { login, customerLogin, forgotPassword, resetPassword, customerForgotPassword, customerResetPassword } from '../services/authService';

/**
 * useLoginMutation — Handles both System User and Customer (Importer) login API calls
 *
 * Usage in Login.jsx:
 *   const loginMutation = useLoginMutation();
 *
 *   const onSubmit = (data) => {
 *     loginMutation.mutate({ isImporter, ...payload }, {
 *       onSuccess: (response) => {
 *         // Save token, redirect user
 *       }
 *     });
 *   };
 */
export const useLoginMutation = () => {
  return useMutation({
    mutationFn: ({ isImporter, ...credentials }) => {
      if (isImporter) {
        return customerLogin(credentials);
      }
      return login(credentials);
    },

    onSuccess: (_, variables) => {
      const userRole = variables?.isImporter ? 'Importer' : 'System User';
      toast.success(`Login successful as ${userRole}! Welcome back.`);
    },

    onError: (error) => {
      const data = error?.response?.data;
      let message = 'Login failed. Please check your credentials.';

      if (typeof data === 'string' && data.trim()) {
        message = data;
      } else if (data?.message) {
        message = data.message;
      } else if (data?.title) {
        message = data.title;
      } else if (data?.error && typeof data.error === 'string') {
        message = data.error;
      } else if (error?.message) {
        message = error.message;
      }

      toast.error(message);
    },
  });
};

/**
 * useForgotPasswordMutation — Handles both System User and Importer forgot password API calls
 * System User: POST /Auth/forgot-password { emailId }
 * Importer:    POST /CustomerAuth/forgot-password { emailId }
 */
export const useForgotPasswordMutation = () => {
  return useMutation({
    mutationFn: ({ isImporter, ...payload }) => {
      if (isImporter) {
        return customerForgotPassword(payload);
      }
      return forgotPassword(payload);
    },
    onSuccess: (data) => {
      const message = typeof data === 'string' ? data : data?.message || 'Password reset request sent! Please check your email for the OTP.';
      toast.success(message);
    },
    onError: (error) => {
      const data = error?.response?.data;
      let message = 'Failed to request password reset. Please check the email address.';
      if (typeof data === 'string' && data.trim()) {
        message = data;
      } else if (data?.message) {
        message = data.message;
      } else if (data?.title) {
        message = data.title;
      }
      toast.error(message);
    },
  });
};

/**
 * useResetPasswordMutation — Handles both System User and Importer reset password API calls
 * System User: POST /Auth/reset-password { emailId, otp, newPassword, confirmPassword }
 * Importer:    POST /CustomerAuth/reset-password { emailId, otp, newPassword, confirmPassword }
 */
export const useResetPasswordMutation = () => {
  return useMutation({
    mutationFn: ({ isImporter, ...payload }) => {
      if (isImporter) {
        return customerResetPassword(payload);
      }
      return resetPassword(payload);
    },
    onSuccess: (data) => {
      const message = typeof data === 'string' ? data : data?.message || 'Password reset successfully! You can now sign in with your new password.';
      toast.success(message);
    },
    onError: (error) => {
      const data = error?.response?.data;
      let message = 'Failed to reset password. Please check your OTP and try again.';
      if (typeof data === 'string' && data.trim()) {
        message = data;
      } else if (data?.message) {
        message = data.message;
      } else if (data?.title) {
        message = data.title;
      }
      toast.error(message);
    },
  });
};
